import { createPortal } from 'react-dom';

import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { CheckCircle, Info, AlertTriangle, X as XIcon } from 'lucide-react';

import { Button } from './button';

type DialogType = 'success' | 'alert' | 'warning' | 'info';

interface BasicDialogProps {
  open: boolean;
  setOpen: (state: boolean) => void;
  title: string;
  body: string;
  type?: DialogType;
  actionText?: string;
  cancelText?: string;
  onActionCallback: () => void;
}

const dialogIcons = {
  success: CheckCircle,
  alert: AlertTriangle,
  warning: AlertTriangle,
  info: Info,
};

const dialogStyles = {
  success: {
    background: 'bg-green-100',
    text: 'text-green-600',
    button: 'bg-green-600 text-white hover:bg-green-500',
  },
  alert: {
    background: 'bg-yellow-100',
    text: 'text-yellow-600',
    button: 'bg-yellow-600 text-white hover:bg-yellow-500',
  },
  warning: {
    background: 'bg-red-100',
    text: 'text-red-600',
    button: 'bg-red-600 text-white hover:bg-red-500',
  },
  info: {
    background: 'bg-blue-100',
    text: 'text-blue-600',
    button: 'bg-blue-600 text-white hover:bg-blue-500',
  },
};

export function BasicDialog({
  open,
  setOpen,
  title,
  body,
  actionText = 'Accept',
  cancelText = 'Cancel',
  type = 'success',
  onActionCallback,
}: BasicDialogProps) {
  const { background, text } = dialogStyles[type];
  const Icon = dialogIcons[type];

  return createPortal(
    <Dialog open={open} onClose={setOpen} className="relative z-100">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/25 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-sm bg-white px-4 pb-4 pt-5 text-left shadow-lg transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
              <Button variant="ghost" size="icon" onClick={() => setOpen(false)}>
                <span className="sr-only">Close</span>
                <XIcon />
              </Button>
            </div>
            <div className="sm:flex sm:items-start">
              <div
                className={`mx-auto flex size-12 shrink-0 items-center justify-center rounded-full ${background} sm:mx-0 sm:size-10`}
              >
                <Icon className={`size-6 ${text}`} aria-hidden="true" />
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <DialogTitle as="h3" className="text-base font-semibold text-gray-900">
                  {title}
                </DialogTitle>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">{body}</p>
                </div>
              </div>
            </div>
            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-2">
              <Button
                onClick={() => {
                  onActionCallback();
                  setOpen(false);
                }}
                variant="destructive"
              >
                {actionText}
              </Button>
              <Button onClick={() => setOpen(false)} variant="ghost-muted" size="sm">
                {cancelText}
              </Button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>,
    document.body
  );
}
